/**
 * The IndexedDB store behind the offline mail cache.
 *
 * Shaped exactly like `api/deviceKey.ts`: a small interface, one real
 * implementation over raw `IDBRequest` plumbing, and a memory implementation
 * for tests — jsdom has no IndexedDB, so the seam is what makes the persister
 * testable at all. The real round trip only exists in a browser, and is proved
 * there (docs/decisions/0058-offline-reading-cache.md).
 *
 * ## One database, one name
 *
 * There is exactly one database, `ols-mail-cache`, and per-account scoping is a
 * property of the *records* (`owner`), not of the database name. That is a
 * deliberate choice made for sign-out: a single fixed name means the purge is
 * `indexedDB.deleteDatabase('ols-mail-cache')` — one call, no enumeration, no
 * `indexedDB.databases()` (which older WebKit does not have), and nothing of
 * any account can be missed because the whole file is gone.
 */

/** The one database name. Purging it purges everything this module ever wrote. */
export const OFFLINE_CACHE_DB = 'ols-mail-cache';
export const OFFLINE_CACHE_VERSION = 1;
const QUERY_STORE = 'queries';
const META_STORE = 'meta';
/** The meta store holds exactly one record, under this key. */
const META_ID = 'current';

/** The account-facing part of a `Session`, as the offline snapshot keeps it. */
export interface OfflineSessionSnapshot {
  account: { id: string; email: string; domain?: string; authHost?: string };
  expiresAt?: string;
  absoluteExpiresAt?: string;
  bound?: boolean;
  admin?: boolean;
}

/** One persisted query. `id` is `${owner}\u0000${hash of key}`. */
export interface OfflineCacheRecord {
  id: string;
  owner: string;
  key: readonly unknown[];
  data: unknown;
  /** The query's own `dataUpdatedAt`: when the server last answered. */
  updatedAt: number;
  /** Approximate serialised size, used for the byte budget. */
  bytes: number;
}

export interface OfflineCacheMeta {
  id?: string;
  owner: string;
  email: string;
  /** The newest `dataUpdatedAt` this cache has ever held. */
  lastSyncedAt: number;
  /**
   * Enough of the session to render the app offline on a cold start.
   *
   * Not a credential and not a token: the session cookies are `__Host-` and
   * `HttpOnly` (ADR 0001), so nothing here can authorise a request. It is a
   * note of *whose* mail this is and when the server said the session ran out,
   * and it is only ever read back while the browser is offline — the moment
   * there is a network, `GET /auth/session` is the authority again.
   */
  session?: OfflineSessionSnapshot | undefined;
}

export interface OfflineCacheStorage {
  readAll(owner: string): Promise<OfflineCacheRecord[]>;
  /** Every record of every owner, newest last. Used by the budget sweep. */
  readIndex(): Promise<Omit<OfflineCacheRecord, 'data' | 'key'>[]>;
  write(records: readonly OfflineCacheRecord[]): Promise<void>;
  remove(ids: readonly string[]): Promise<void>;
  readMeta(): Promise<OfflineCacheMeta | undefined>;
  writeMeta(meta: OfflineCacheMeta): Promise<void>;
  /** Delete the whole database. Resolves false when it did not go. */
  destroy(): Promise<boolean>;
}

/* ------------------------------------------------------------ real storage */

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
  });
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const open = indexedDB.open(OFFLINE_CACHE_DB, OFFLINE_CACHE_VERSION);
    open.onupgradeneeded = () => {
      const db = open.result;
      if (!db.objectStoreNames.contains(QUERY_STORE)) {
        const store = db.createObjectStore(QUERY_STORE, { keyPath: 'id' });
        store.createIndex('by-owner', 'owner', { unique: false });
        store.createIndex('by-updated', 'updatedAt', { unique: false });
      }
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE, { keyPath: 'id' });
      }
    };
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(open.error ?? new Error('IndexedDB open failed'));
    open.onblocked = () => reject(new Error('IndexedDB open blocked'));
  });
}

function done(tx: IDBTransaction): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
  });
}

export function createIndexedDbOfflineCacheStorage(): OfflineCacheStorage {
  async function withDb<T>(run: (db: IDBDatabase) => Promise<T>): Promise<T> {
    const db = await openDatabase();
    try {
      return await run(db);
    } finally {
      db.close();
    }
  }

  return {
    readAll: (owner) =>
      withDb(async (db) => {
        const tx = db.transaction(QUERY_STORE, 'readonly');
        const index = tx.objectStore(QUERY_STORE).index('by-owner');
        const rows = await request<OfflineCacheRecord[]>(
          index.getAll(IDBKeyRange.only(owner)) as IDBRequest<OfflineCacheRecord[]>,
        );
        await done(tx);
        return rows;
      }),

    readIndex: () =>
      withDb(async (db) => {
        const tx = db.transaction(QUERY_STORE, 'readonly');
        const rows = await request<OfflineCacheRecord[]>(
          tx.objectStore(QUERY_STORE).getAll() as IDBRequest<OfflineCacheRecord[]>,
        );
        await done(tx);
        return rows.map(({ id, owner, updatedAt, bytes }) => ({ id, owner, updatedAt, bytes }));
      }),

    write: (records) =>
      withDb(async (db) => {
        const tx = db.transaction(QUERY_STORE, 'readwrite');
        const store = tx.objectStore(QUERY_STORE);
        for (const record of records) {
          try {
            store.put(record);
          } catch {
            // Not structured-cloneable (a function, a Symbol). Skipping one
            // record is right; aborting the whole flush over it is not.
          }
        }
        await done(tx);
      }),

    remove: (ids) =>
      withDb(async (db) => {
        if (ids.length === 0) return;
        const tx = db.transaction(QUERY_STORE, 'readwrite');
        const store = tx.objectStore(QUERY_STORE);
        for (const id of ids) store.delete(id);
        await done(tx);
      }),

    readMeta: () =>
      withDb(async (db) => {
        const tx = db.transaction(META_STORE, 'readonly');
        const row = await request<OfflineCacheMeta | undefined>(
          tx.objectStore(META_STORE).get(META_ID) as IDBRequest<OfflineCacheMeta | undefined>,
        );
        await done(tx);
        return row;
      }),

    writeMeta: (meta) =>
      withDb(async (db) => {
        const tx = db.transaction(META_STORE, 'readwrite');
        tx.objectStore(META_STORE).put({ ...meta, id: META_ID });
        await done(tx);
      }),

    destroy: () =>
      new Promise<boolean>((resolve) => {
        let settled = false;
        const finish = (ok: boolean): void => {
          if (settled) return;
          settled = true;
          resolve(ok);
        };
        let req: IDBOpenDBRequest;
        try {
          req = indexedDB.deleteDatabase(OFFLINE_CACHE_DB);
        } catch {
          finish(false);
          return;
        }
        req.onsuccess = () => finish(true);
        req.onerror = () => finish(false);
        // Another tab still holds the database open. It will be deleted when
        // that tab lets go, but this sign-out cannot say that it *has* been —
        // and saying so falsely is the one thing this must never do.
        req.onblocked = () => finish(false);
      }),
  };
}

/* ---------------------------------------------------------- memory storage */

export interface MemoryOfflineCacheStorage extends OfflineCacheStorage {
  records(): OfflineCacheRecord[];
  destroyed: boolean;
  /** Make the next `destroy()` report failure, as a blocked database would. */
  failNextDestroy(): void;
  /** Push every record this far into the past. */
  ageAll(ms: number): void;
}

export function createMemoryOfflineCacheStorage(): MemoryOfflineCacheStorage {
  let rows = new Map<string, OfflineCacheRecord>();
  let meta: OfflineCacheMeta | undefined;
  let failDestroy = false;

  const storage: MemoryOfflineCacheStorage = {
    destroyed: false,
    records: () => [...rows.values()],
    failNextDestroy: () => {
      failDestroy = true;
    },
    ageAll: (ms) => {
      for (const row of rows.values()) row.updatedAt -= ms;
    },
    readAll: async (owner) => [...rows.values()].filter((row) => row.owner === owner),
    readIndex: async () =>
      [...rows.values()].map(({ id, owner, updatedAt, bytes }) => ({
        id,
        owner,
        updatedAt,
        bytes,
      })),
    write: async (records) => {
      for (const record of records) rows.set(record.id, { ...record });
    },
    remove: async (ids) => {
      for (const id of ids) rows.delete(id);
    },
    readMeta: async () => meta,
    writeMeta: async (next) => {
      meta = { ...next, id: META_ID };
    },
    destroy: async () => {
      if (failDestroy) {
        failDestroy = false;
        return false;
      }
      rows = new Map();
      meta = undefined;
      storage.destroyed = true;
      return true;
    },
  };
  return storage;
}

/* ------------------------------------------------------------------- seam */

let override: OfflineCacheStorage | undefined;
let real: OfflineCacheStorage | undefined;

/** True when this browser can hold a persisted cache at all. */
export function isOfflineCacheSupported(): boolean {
  if (override !== undefined) return true;
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch {
    // Storage blocked (some private-mode configurations throw on access).
    return false;
  }
}

export function getOfflineCacheStorage(): OfflineCacheStorage | undefined {
  if (override !== undefined) return override;
  if (!isOfflineCacheSupported()) return undefined;
  real ??= createIndexedDbOfflineCacheStorage();
  return real;
}

/** Tests only. */
export function setOfflineCacheStorage(storage: OfflineCacheStorage): void {
  override = storage;
}

/** Tests only. */
export function resetOfflineCacheStorage(): void {
  override = undefined;
}
